import { CodedProperty, NamedProperty, ResolutionProperty } from "sprint-common-web";

import { getCloudAssetDetailsPageHref } from "../../../../assets/AssetsRoutes";
import { resolveOrderContentTitle } from "../../../../common/api/orders-api/order/ContentReference";
import { NoteItem } from "../../../../common/components/notes/hooks/state/NoteItem";
import { DynamicSubtitleType } from "../../../../common/types/DynamicSubtitleType";
import { RecordType } from "../../../../common/types/RecordType";
import { OrderAssetAvailability } from "../../../common/types/OrderAssetAvailability";
import { OrderAssetStorageType } from "../../../common/types/OrderAssetStorageType";
import { OrderAssetType, VideoFileType, AudioFileType, OrderDashboardItem } from "../OrderDashboardItem";

export type AssetsAvailabilityItem =
    | VideoAssetAvailabilityItem
    | SoundtrackAssetAvailabilityItem
    | SubtitleAssetAvailabilityItem;

interface AssetsAvailabilityItemBase {
    id: string;
    type: RecordType;
    name: string;
    bucketName?: string;
    filePath: string;
    fileType: OrderAssetType | undefined;
    notes: NoteItem[];
}

export interface VideoAssetAvailabilityItem extends AssetsAvailabilityItemBase {
    type: RecordType.Video;
    assetInformation: AssetInformation;
    fileType: VideoFileType | undefined;
    aspectRatio?: NamedProperty;
    subtitle?: CodedProperty;
    nonMatchingVideos: {
        aspectRatio: NamedProperty;
        resolution: ResolutionProperty;
        subtitle: CodedProperty;
        assetId: string;
    }[];
    encodeSpecification: {
        targetAspectRatio: NamedProperty;
    };
    sourceResolution?: ResolutionProperty;
    targetResolution?: ResolutionProperty;
}

export interface SoundtrackAssetAvailabilityItem extends AssetsAvailabilityItemBase {
    type: RecordType.Audio;
    assetInformation: AssetLanguageInformation;
    fileType: AudioFileType | undefined;
}

export interface SubtitleAssetAvailabilityItem extends AssetsAvailabilityItemBase {
    type: RecordType.Subtitle;
    fileType: DynamicSubtitleType | undefined;
    assetInformation: AssetLanguageInformation;
}

type AssetLanguageInformation = CodedProperty & AssetInformation;

interface AssetInformation {
    assetId: string | undefined;
    assetHref: string | undefined;
    availability: OrderAssetAvailability;
    storageType?: OrderAssetStorageType;
}

export const mapToAssetsAvailabilityItems = (orderDashboardItem: OrderDashboardItem): AssetsAvailabilityItem[] => {
    const video: VideoAssetAvailabilityItem = {
        id: orderDashboardItem.id,
        name: resolveOrderContentTitle(orderDashboardItem.content),
        bucketName:
            orderDashboardItem.cloudAssets.video.availability === OrderAssetAvailability.FullMatch
                ? orderDashboardItem.cloudAssets.video.bucket
                : "-",
        filePath: orderDashboardItem.cloudAssets.video.filePath ?? "-",
        type: RecordType.Video,
        assetInformation: {
            availability: orderDashboardItem.cloudAssets.video.availability,
            assetId: orderDashboardItem.cloudAssets.video.assetId,
            storageType: orderDashboardItem.cloudAssets.video.storageType,
            assetHref:
                orderDashboardItem.cloudAssets.video.assetId &&
                getCloudAssetDetailsPageHref(orderDashboardItem.cloudAssets.video.assetId)
        },
        subtitle: orderDashboardItem.cloudAssets.video.subtitle,
        aspectRatio: orderDashboardItem.cloudAssets.video.aspectRatio,
        encodeSpecification: orderDashboardItem.encodeSpecification,
        sourceResolution: orderDashboardItem.cloudAssets.video.sourceResolution,
        targetResolution: orderDashboardItem.cloudAssets.video.targetResolution,
        fileType: orderDashboardItem.cloudAssets.video.fileType,
        nonMatchingVideos: orderDashboardItem.cloudAssets.video.nonMatchingVideos.map((nmVideo) => ({
            aspectRatio: nmVideo.aspectRatio,
            resolution: nmVideo.resolution,
            subtitle: nmVideo.subtitle,
            assetId: nmVideo.assetId
        })),
        notes: orderDashboardItem.assetNotes.filter(
            (note) => note.subjectId === orderDashboardItem.cloudAssets.video.assetId
        )
    };

    const soundtracks: SoundtrackAssetAvailabilityItem[] = orderDashboardItem.cloudAssets.soundtracks.map(
        (soundtrack) => {
            return {
                id: orderDashboardItem.id,
                name: soundtrack.language.name,
                bucketName: soundtrack.availability === OrderAssetAvailability.FullMatch ? soundtrack.bucket : "-",
                filePath: soundtrack.filePath ?? "-",
                fileType: soundtrack.fileType,
                type: RecordType.Audio,
                assetInformation: {
                    ...soundtrack.language,
                    assetHref: soundtrack.assetId && getCloudAssetDetailsPageHref(soundtrack.assetId),
                    assetId: soundtrack.assetId,
                    availability: soundtrack.availability,
                    storageType: soundtrack.storageType
                },
                notes: orderDashboardItem.assetNotes.filter((note) => note.subjectId === soundtrack.assetId)
            };
        }
    );

    const subtitles: SubtitleAssetAvailabilityItem[] = orderDashboardItem.cloudAssets.subtitles.map((subtitle) => {
        return {
            id: orderDashboardItem.id,
            name: subtitle.language.name,
            bucketName: subtitle.assetAvailability === OrderAssetAvailability.FullMatch ? subtitle.bucket : "-",
            filePath: subtitle.filePath ?? "-",
            type: RecordType.Subtitle,
            assetInformation: {
                ...subtitle.language,
                assetHref: subtitle.assetId && getCloudAssetDetailsPageHref(subtitle.assetId),
                assetId: subtitle.assetId,
                availability: subtitle.assetAvailability,
                isEmbedded: subtitle.isEmbedded,
                storageType: subtitle.storageType
            },
            fileType: subtitle.subtitleType,
            notes: orderDashboardItem.assetNotes.filter((note) => note.subjectId === subtitle.assetId)
        };
    });

    return [video, ...soundtracks, ...subtitles];
};
